// Gebruiker menu surfaces (LEGACY-COMPARISON L6/L7) — Mijn tickets / Mijn overeenkomsten / Mijn consultant /
// Mijn cases. These read the signed-in user's own data via the tenant-worker `api` (RLS-scoped). Real where
// a backend exists; honest empty-state where it does not (no tables fabricated). shadcn, behind real auth.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { humanizeEnum } from "../../lib/enum-labels";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type Row = Record<string, unknown>;
const fmt = (d: unknown) => (d ? new Date(String(d)).toLocaleDateString() : "—");
function Empty({ text }: { text: string }) {
  return <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{text}</CardContent></Card>;
}

// ── Mijn tickets (REAL — tickets assigned to me) ───────────────────────────────────────────────────
export function MijnTicketsRoute() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["me-tickets"], queryFn: () => api.listMyTickets() as Promise<Row[]> });
  const columns: Column<Row>[] = [
    { key: "title", header: t("me.tickets.col.title"), render: (r) => <span className="font-medium">{String(r.title ?? "—")}</span> },
    { key: "state", header: t("me.tickets.col.status"), render: (r) => <Badge variant="secondary">{humanizeEnum(r.currentState ?? r.lifecycle)}</Badge> },
    { key: "priority", header: t("me.tickets.col.priority"), render: (r) => String(r.priority ?? "—") },
    { key: "due", header: t("me.tickets.col.due"), render: (r) => <span className="text-muted-foreground">{fmt(r.dueAt)}</span> },
  ];
  return (
    <section className="flex flex-col gap-4">
      <PageHeader title={t("me.tickets.title")} description={t("me.tickets.sub")} />
      <DataTable<Row> columns={columns} rows={data ?? []} loading={isLoading} getRowId={(r) => String(r.id)} empty={t("me.tickets.empty")} />
    </section>
  );
}

// ── Mijn overeenkomsten (REAL — Art.28 processor agreements + shared DPAs) ──────────────────────────
export function MijnOvereenkomstenRoute() {
  const { t } = useI18n();
  const pa = useQuery({ queryKey: ["me-processor-agreements"], queryFn: () => api.listProcessorAgreements() as Promise<Row[]>, retry: false });
  const dpa = useQuery({ queryKey: ["me-shared-dpas"], queryFn: () => api.listSharedDpas() as Promise<Row[]>, retry: false });
  const rows = [...(pa.data ?? []), ...(dpa.data ?? [])];
  const columns: Column<Row>[] = [
    { key: "name", header: t("me.agr.col.name"), render: (r) => <span className="font-medium">{String(r.name ?? r.agreementName ?? r.dpaVersionLabel ?? r.versionLabel ?? "Overeenkomst")}</span> },
    { key: "status", header: t("me.agr.col.status"), render: (r) => <Badge variant="secondary">{humanizeEnum(r.status ?? r.confirmationStatus)}</Badge> },
    { key: "updated", header: t("me.agr.col.updated"), render: (r) => <span className="text-muted-foreground">{fmt(r.updatedAt ?? r.createdAt)}</span> },
  ];
  return (
    <section className="flex flex-col gap-4">
      <PageHeader title={t("me.agr.title")} description={t("me.agr.sub")} />
      {rows.length === 0 && !pa.isLoading && !dpa.isLoading
        ? <Empty text={t("me.agr.empty")} />
        : <DataTable<Row> columns={columns} rows={rows} loading={pa.isLoading || dpa.isLoading} getRowId={(r) => String(r.id ?? r.name ?? r.dpaVersionLabel ?? Math.random())} empty={t("me.agr.empty")} />}
    </section>
  );
}

// ── Mijn consultant (REAL where present — the tenant's consultant engagements) ──────────────────────
export function MijnConsultantRoute() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["me-consultant"], queryFn: () => api.listConsultantEngagements() as Promise<Row[]>, retry: false });
  const rows = Array.isArray(data) ? data : [];
  return (
    <section className="flex flex-col gap-4">
      <PageHeader title={t("me.consultant.title")} description={t("me.consultant.sub")} />
      {rows.length === 0 && !isLoading ? (
        <Card>
          <CardHeader><CardTitle className="text-base">{t("me.consultant.none.title")}</CardTitle></CardHeader>
          <CardContent className="flex flex-col items-start gap-3 text-sm text-muted-foreground">
            {t("me.consultant.none.body")}
            <Button asChild variant="outline" size="sm"><Link to="/consultants">{t("me.consultant.browse")}</Link></Button>
          </CardContent>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">{rows.map((r, i) => (
          <li key={String(r.id ?? i)}><Card><CardContent className="flex items-center justify-between py-4 text-sm">
            <span className="font-medium">{String(r.consultantOrgName ?? r.title ?? r.engagementTitle ?? "Engagement")}</span>
            <Badge variant="secondary">{humanizeEnum(r.status)}</Badge>
          </CardContent></Card></li>
        ))}</ul>
      )}
    </section>
  );
}

// ── Mijn cases (HONEST EMPTY — no support-case backend) ─────────────────────────────────────────────
export function MijnCasesRoute() {
  const { t } = useI18n();
  return (
    <section className="flex flex-col gap-4">
      <PageHeader title={t("me.cases.title")} description={t("me.cases.sub")} />
      <Empty text={t("me.cases.empty")} />
    </section>
  );
}
